import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Plus, RefreshCw, Search } from "lucide-react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { contractAdminService } from "@/features/contracts/services/contract-admin.service";
import { contractService } from "@/features/contracts/services/contract.service";
import type { ContractResponseDTO } from "@/features/contracts/types/contract.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";
import { toast } from "sonner";
import { ContractsTable } from "@/features/contracts/components/ContractsTable";
import { ContractFormDialog } from "@/features/contracts/components/ContractFormDialog";
import { ContractDeleteDialog } from "@/features/contracts/components/ContractDeleteDialog";
import { ContractImageDialog } from "@/features/contracts/components/ContractImageDialog";
import { ClientSearchInput } from "@/features/contracts/components/ClientSearchInput";
import type { ClientOption } from "@/features/contracts/components/ClientSearchInput";
import type { ContractParentOption } from "@/features/contracts/types/contract.types";
import { clientAdminService } from "@/features/clients/services/client-admin.service";
import { clientService } from "@/features/clients/services/client.service";
import {
  fileToBase64,
  fileToBase64WithPrefix,
  isValidImageFile,
  isValidFileSize,
} from "@/features/contracts/utils/contract.utils";

const DIALOG_MODE = {
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
  VIEW_IMAGE: "view-image",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

const contractSchema = z.object({
  contractNumber: z
    .string()
    .min(1, "Número do contrato é obrigatório")
    .max(100),
  clientId: z.number().min(1, "ID do cliente é obrigatório"),
  parentingId: z.number().min(1).optional(),
  contractImage: z.string().optional(),
});

type ContractFormData = z.infer<typeof contractSchema>;

export function ContratosPage() {
  const { isAdmin } = useAuth();

  const [contracts, setContracts] = useState<ContractResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientSuggestions, setClientSuggestions] = useState<ClientOption[]>(
    [],
  );
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clientSearchControllerRef = useRef<AbortController | null>(null);

  const [filterContractNumber, setFilterContractNumber] = useState("");

  // form — client search
  const [formClientTerm, setFormClientTerm] = useState("");
  const [formClientOptions, setFormClientOptions] = useState<ClientOption[]>(
    [],
  );
  const [formClientLoading, setFormClientLoading] = useState(false);
  const [formSelectedClientId, setFormSelectedClientId] = useState<
    number | null
  >(null);
  const formClientDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const formClientControllerRef = useRef<AbortController | null>(null);

  // form — parent contract search
  const [formParentTerm, setFormParentTerm] = useState("");
  const [formParentOptions, setFormParentOptions] = useState<
    ContractParentOption[]
  >([]);
  const [formParentLoading, setFormParentLoading] = useState(false);
  const [formSelectedParentId, setFormSelectedParentId] = useState<
    number | null
  >(null);
  const formParentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selectedContract, setSelectedContract] =
    useState<ContractResponseDTO | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);

  const form = useForm<ContractFormData>({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      contractNumber: "",
      clientId: 0,
      parentingId: undefined,
      contractImage: "",
    },
  });

  const loadContracts = useCallback(
    async (page: number = 0) => {
      setLoading(true);
      try {
        const searchParams = {
          clientId: selectedClientId ?? undefined,
          contractNumber: filterContractNumber || undefined,
          page,
          size: pageSize,
          sortBy: "contractNumber",
          sortDirection: "ASC" as const,
        };
        const response = isAdmin
          ? await contractAdminService.search(searchParams)
          : await contractService.search(searchParams);
        setContracts(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setCurrentPage(page);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao carregar contratos"));
      } finally {
        setLoading(false);
      }
    },
    [pageSize, selectedClientId, filterContractNumber, isAdmin],
  );

  useEffect(() => {
    // Recarrega só ao trocar pageSize; filtros disparam via botão Buscar.
    // loadContracts captura filtros via closure — intencional. Padrão legado
    // Família A; migrar para useContractList em refator futuro.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadContracts(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const filteredContracts = useMemo(() => {
    if (!searchTerm) return contracts;
    const search = searchTerm.toLowerCase();
    return contracts.filter(
      (c) =>
        c.contractNumber.toLowerCase().includes(search) ||
        c.clientName.toLowerCase().includes(search),
    );
  }, [contracts, searchTerm]);

  const handleSearch = useCallback(() => {
    loadContracts(0);
  }, [loadContracts]);

  const handleClientSearch = useCallback(
    (term: string) => {
      setClientSearchTerm(term);
      setSelectedClientId(null);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      clientSearchControllerRef.current?.abort();

      if (!term.trim()) {
        setClientSuggestions([]);
        setClientSearchLoading(false);
        return;
      }

      setClientSearchLoading(true);

      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        clientSearchControllerRef.current = controller;
        try {
          const searchParams = {
            name: term,
            size: 8,
            sortBy: "name" as const,
            sortDirection: "ASC" as const,
          };
          const response = isAdmin
            ? await clientAdminService.search(searchParams, {
                signal: controller.signal,
              })
            : await clientService.search(searchParams, {
                signal: controller.signal,
              });
          if (controller.signal.aborted) return;
          setClientSuggestions(
            response.content.map((c) => ({
              id: c.id,
              name: c.name,
              document: c.document,
            })),
          );
        } catch {
          if (controller.signal.aborted) return;
          setClientSuggestions([]);
        } finally {
          if (!controller.signal.aborted) setClientSearchLoading(false);
        }
      }, 350);
    },
    [isAdmin],
  );

  const handleClientSelect = useCallback((client: ClientOption) => {
    setClientSearchTerm(client.name);
    setSelectedClientId(client.id);
    setClientSuggestions([]);
  }, []);

  const handleClientClear = useCallback(() => {
    setClientSearchTerm("");
    setSelectedClientId(null);
    setClientSuggestions([]);
  }, []);

  const handleFormClientSearch = useCallback(
    (term: string) => {
      setFormClientTerm(term);
      setFormSelectedClientId(null);
      form.setValue("clientId", 0);

      if (formClientDebounceRef.current)
        clearTimeout(formClientDebounceRef.current);
      formClientControllerRef.current?.abort();

      if (!term.trim()) {
        setFormClientOptions([]);
        setFormClientLoading(false);
        return;
      }

      setFormClientLoading(true);
      formClientDebounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        formClientControllerRef.current = controller;
        try {
          const params = {
            name: term,
            size: 8,
            sortBy: "name" as const,
            sortDirection: "ASC" as const,
          };
          const response = isAdmin
            ? await clientAdminService.search(params, {
                signal: controller.signal,
              })
            : await clientService.search(params, {
                signal: controller.signal,
              });
          if (controller.signal.aborted) return;
          setFormClientOptions(
            response.content.map((c) => ({
              id: c.id,
              name: c.name,
              document: c.document,
            })),
          );
        } catch {
          if (controller.signal.aborted) return;
          setFormClientOptions([]);
        } finally {
          if (!controller.signal.aborted) setFormClientLoading(false);
        }
      }, 350);
    },
    [form, isAdmin],
  );

  const handleFormClientSelect = useCallback(
    (client: ClientOption) => {
      setFormClientTerm(client.name);
      setFormSelectedClientId(client.id);
      setFormClientOptions([]);
      form.setValue("clientId", client.id);
    },
    [form],
  );

  const handleFormClientClear = useCallback(() => {
    setFormClientTerm("");
    setFormSelectedClientId(null);
    setFormClientOptions([]);
    form.setValue("clientId", 0);
  }, [form]);

  const handleFormParentSearch = useCallback(
    (term: string) => {
      setFormParentTerm(term);
      setFormSelectedParentId(null);
      form.setValue("parentingId", undefined);

      if (formParentDebounceRef.current)
        clearTimeout(formParentDebounceRef.current);

      if (!term.trim()) {
        setFormParentOptions([]);
        setFormParentLoading(false);
        return;
      }

      setFormParentLoading(true);
      formParentDebounceRef.current = setTimeout(async () => {
        try {
          const params = {
            contractNumber: term,
            size: 8,
            sortBy: "contractNumber",
            sortDirection: "ASC" as const,
          };
          const response = isAdmin
            ? await contractAdminService.search(params)
            : await contractService.search(params);
          setFormParentOptions(
            response.content
              .filter((c) => c.id !== selectedContract?.id)
              .map((c) => ({
                id: c.id,
                contractNumber: c.contractNumber,
                clientName: c.clientName,
              })),
          );
        } catch {
          setFormParentOptions([]);
        } finally {
          setFormParentLoading(false);
        }
      }, 350);
    },
    [form, isAdmin, selectedContract],
  );

  const handleFormParentSelect = useCallback(
    (contract: ContractParentOption) => {
      setFormParentTerm(contract.contractNumber);
      setFormSelectedParentId(contract.id);
      setFormParentOptions([]);
      form.setValue("parentingId", contract.id);
    },
    [form],
  );

  const handleFormParentClear = useCallback(() => {
    setFormParentTerm("");
    setFormSelectedParentId(null);
    setFormParentOptions([]);
    form.setValue("parentingId", undefined);
  }, [form]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!isValidImageFile(file)) {
        toast.error("Por favor, selecione apenas arquivos de imagem");
        return;
      }

      if (!isValidFileSize(file, 5)) {
        toast.error("A imagem deve ter no máximo 5MB");
        return;
      }

      try {
        const [base64Data, base64WithPrefix] = await Promise.all([
          fileToBase64(file),
          fileToBase64WithPrefix(file),
        ]);
        form.setValue("contractImage", base64Data);
        setPhotoPreview(base64WithPrefix);
        setPhotoRemoved(false);
      } catch {
        toast.error("Erro ao processar a imagem");
      }
    },
    [form],
  );

  const handleViewImage = useCallback((contract: ContractResponseDTO) => {
    setSelectedContract(contract);
    setDialogMode(DIALOG_MODE.VIEW_IMAGE);
    setDialogContent(DIALOG_MODE.VIEW_IMAGE);
  }, []);

  const handleRemoveImage = useCallback(() => {
    form.setValue("contractImage", "");
    setPhotoPreview(null);
    setPhotoRemoved(true);
  }, [form]);

  const handleOpenDialog = useCallback(
    (mode: DialogMode, contract?: ContractResponseDTO) => {
      setDialogMode(mode);
      setDialogContent(mode);
      setSelectedContract(contract ?? null);

      if (mode === DIALOG_MODE.EDIT && contract) {
        form.reset({
          contractNumber: contract.contractNumber,
          clientId: contract.clientId,
          parentingId: contract.parentingId ?? undefined,
          contractImage: "",
        });
        setPhotoPreview(contract.contractImageUrl ?? null);
        setPhotoRemoved(false);
        setFormClientTerm(contract.clientName);
        setFormSelectedClientId(contract.clientId);
        setFormClientOptions([]);
        setFormParentTerm(contract.parentingContractNumber ?? "");
        setFormSelectedParentId(contract.parentingId ?? null);
        setFormParentOptions([]);
      } else if (mode === DIALOG_MODE.CREATE) {
        form.reset({
          contractNumber: "",
          clientId: 0,
          parentingId: undefined,
          contractImage: "",
        });
        setPhotoPreview(null);
        setPhotoRemoved(false);
        setFormClientTerm("");
        setFormSelectedClientId(null);
        setFormClientOptions([]);
        setFormParentTerm("");
        setFormSelectedParentId(null);
        setFormParentOptions([]);
      }
    },
    [form],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogMode(null);
    setTimeout(() => {
      setDialogContent(null);
      setSelectedContract(null);
      setPhotoPreview(null);
      setPhotoRemoved(false);
      setFormClientTerm("");
      setFormSelectedClientId(null);
      setFormClientOptions([]);
      setFormParentTerm("");
      setFormSelectedParentId(null);
      setFormParentOptions([]);
      form.reset();
    }, 300);
  }, [form]);

  const onSubmit = useCallback(
    async (data: ContractFormData) => {
      setLoading(true);
      try {
        if (dialogMode === DIALOG_MODE.CREATE) {
          const payload = {
            contractNumber: data.contractNumber,
            clientId: data.clientId,
            parentingId: data.parentingId,
            contractImage: data.contractImage || undefined,
          };
          const created = isAdmin
            ? await contractAdminService.create(payload)
            : await contractService.create(payload);
          toast.success("Contrato criado com sucesso!");
          setContracts((prev) => [created, ...prev]);
          setTotalElements((prev) => prev + 1);
        } else if (dialogMode === DIALOG_MODE.EDIT && selectedContract) {
          const imageValue = photoRemoved
            ? null
            : data.contractImage || undefined;
          const payload = {
            contractNumber: data.contractNumber,
            clientId: data.clientId,
            parentingId: data.parentingId,
            contractImage: imageValue,
          };
          const updated = isAdmin
            ? await contractAdminService.update(selectedContract.id, payload)
            : await contractService.update(selectedContract.id, payload);
          toast.success("Contrato atualizado com sucesso!");
          setContracts((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c)),
          );
        }
        handleCloseDialog();
      } catch (error: unknown) {
        if (!applyFieldErrorsToForm(error, form)) {
          toast.error(getApiErrorMessage(error, "Erro ao salvar contrato"));
        }
      } finally {
        setLoading(false);
      }
    },
    [
      dialogMode,
      selectedContract,
      photoRemoved,
      handleCloseDialog,
      isAdmin,
      form,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedContract) return;

    setLoading(true);
    try {
      if (isAdmin) {
        await contractAdminService.remove(selectedContract.id);
      } else {
        await contractService.remove(selectedContract.id);
      }
      toast.success("Contrato excluído com sucesso!");

      const updated = contracts.filter((c) => c.id !== selectedContract.id);
      setContracts(updated);

      const newTotal = totalElements - 1;
      setTotalElements(newTotal);
      setTotalPages(Math.ceil(newTotal / pageSize));

      if (updated.length === 0 && currentPage > 0) {
        await loadContracts(currentPage - 1);
      }

      handleCloseDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir contrato"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedContract,
    contracts,
    totalElements,
    pageSize,
    currentPage,
    handleCloseDialog,
    loadContracts,
    isAdmin,
  ]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        loadContracts(newPage);
      }
    },
    [totalPages, loadContracts],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Contratos</h1>
          <p className="text-muted-foreground">
            Gerencie os contratos de locação
          </p>
        </div>
        <Can permission={ACTIONS.contracts.create}>
          <Button
            className="gap-2"
            onClick={() => handleOpenDialog(DIALOG_MODE.CREATE)}
            disabled={loading}
          >
            <Plus className="w-4 h-4" />
            Novo Contrato
          </Button>
        </Can>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros de Busca</CardTitle>
          <CardDescription>
            Filtre contratos por cliente ou número
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Cliente</label>
              <ClientSearchInput
                value={clientSearchTerm}
                selectedId={selectedClientId}
                options={clientSuggestions}
                loading={clientSearchLoading}
                onSearch={handleClientSearch}
                onSelect={handleClientSelect}
                onClear={handleClientClear}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Número do Contrato</label>
              <Input
                placeholder="CTR-2024-001"
                value={filterContractNumber}
                onChange={(e) => setFilterContractNumber(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleSearch} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                handleClientClear();
                setFilterContractNumber("");
              }}
            >
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Lista de Contratos</CardTitle>
              <CardDescription>
                {loading
                  ? "Carregando..."
                  : `Mostrando ${filteredContracts.length} de ${totalElements} contratos`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filtrar na tabela..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="md:max-w-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadContracts(currentPage)}
                disabled={loading}
                title="Atualizar lista"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading}
              >
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
                <option value={100}>100 / página</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && contracts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando contratos...
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum contrato encontrado
            </div>
          ) : (
            <ContractsTable
              contracts={filteredContracts}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onEdit={(contract) =>
                handleOpenDialog(DIALOG_MODE.EDIT, contract)
              }
              onDelete={(contract) =>
                handleOpenDialog(DIALOG_MODE.DELETE, contract)
              }
              onViewImage={handleViewImage}
              onPageChange={handlePageChange}
            />
          )}
        </CardContent>
      </Card>

      <ContractFormDialog
        open={
          dialogMode === DIALOG_MODE.CREATE || dialogMode === DIALOG_MODE.EDIT
        }
        mode={dialogContent === DIALOG_MODE.CREATE ? "create" : "edit"}
        loading={loading}
        form={form}
        photoPreview={photoPreview}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
        onFileChange={handleFileChange}
        onRemoveImage={handleRemoveImage}
        clientSearchTerm={formClientTerm}
        clientSearchOptions={formClientOptions}
        clientSearchLoading={formClientLoading}
        selectedClientId={formSelectedClientId}
        onClientSearch={handleFormClientSearch}
        onClientSelect={handleFormClientSelect}
        onClientClear={handleFormClientClear}
        parentSearchTerm={formParentTerm}
        parentSearchOptions={formParentOptions}
        parentSearchLoading={formParentLoading}
        selectedParentId={formSelectedParentId}
        onParentSearch={handleFormParentSearch}
        onParentSelect={handleFormParentSelect}
        onParentClear={handleFormParentClear}
      />

      <ContractDeleteDialog
        open={dialogMode === DIALOG_MODE.DELETE}
        loading={loading}
        contract={selectedContract}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
      />

      <ContractImageDialog
        open={dialogMode === DIALOG_MODE.VIEW_IMAGE}
        contract={selectedContract}
        onClose={handleCloseDialog}
      />
    </div>
  );
}
